import { Empty, IconFont, Input, Menu, Text } from '@byecode/ui'
import type {
    BlockAbstract,
    ButtonBlockAbstract,
    ContainerBlockAbstract,
    FloatBlockAbstract,
    IconBlockAbstract,
    TextBlockAbstract
} from '@lighthouse/core'
import { FLEX_ALIGN } from '@lighthouse/core'
import {
    findBlockById,
    findNormalOrSyncBlock,
    getCurrentBlockChildren,
    Group4ByecodeUi,
    ListItem4ByecodeUi,
    TagIcon,
    useAtomAction,
    useAtomData
} from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import { useAtomCallback } from 'jotai/utils'
import { clone, mergeDeepRight, reduce } from 'rambda'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { toggleFloatBoxAtom } from '@/atoms/application/action'
import { pageSyncComponentsAtom, syncComponentsAtom } from '@/atoms/application/state'
import { viewIndependentDataAtomFamily } from '@/atoms/dataSource/state'
import { createBlockAtom, initContainerRuntimeStateAtom, updateRemoteBlockAtom } from '@/atoms/page/action'
import {
    blocksAtom,
    containerRuntimeStateAtomFamily,
    lastPageOfStackAtom,
    pageInternalStateAtomFamily,
    selectedBlockCustomViewAtom
} from '@/atoms/page/state'
import { generateFloatBlock } from '@/constants/Block/generate/float'
import { getBlockInitConfig } from '@/constants/Block/help'
import { useNodeToolbarActions } from '@/hooks/layoutEngine/useNodeToolbarActions'
import { useCurrentAppId, useMainBreakPoint } from '@/hooks/useApplication'
import { createSyncBlock } from '@/services'
import { NODE_UPDATE_ACTION, pageUndoRedoController } from '@/utils/undoRedo/page/controller'

interface FloatingControlProps {
    value?: string[]
    blockId: string
    onChange: (v: string[]) => void
}

const SCxContainer = styled.div`
    border-radius: 8px;
    margin: 8px;
    border: 1px dashed var(--color-gray-200);
    background-color: var(--color-gray-25);
`

type IncludeFloatBoxBlockOfBlock = ContainerBlockAbstract | TextBlockAbstract | IconBlockAbstract | ButtonBlockAbstract

export const FloatingControl: React.FunctionComponent<FloatingControlProps> = ({ value, blockId, onChange }) => {
    const stack = useAtomData(lastPageOfStackAtom)
    const { pageId = '', stackId = '' } = stack ?? {}

    const selectNode = useAtomData(
        pageInternalStateAtomFamily(stackId),
        useCallback(s => s.selectedNodes?.[0], [])
    )

    const { scope: currentScope } = selectNode ?? {}
    const mainBreakPoint = useMainBreakPoint()
    const appId = useCurrentAppId()
    const blocks = useAtomData(
        blocksAtom,
        useCallback(s => s[pageId] ?? [], [pageId])
    )
    const syncComponents = useAtomData(pageSyncComponentsAtom)
    const { run: setSyncBlocks } = useAtomAction(syncComponentsAtom)
    const { run: setBlocks } = useAtomAction(blocksAtom)
    const { run: createBlock } = useAtomAction(createBlockAtom)
    const { run: openFloatBox } = useAtomAction(toggleFloatBoxAtom)
    const { run: updateRemoteBlock } = useAtomAction(updateRemoteBlockAtom)

    const [syncContainerBlock, containerBlock, isSyncScope] = useMemo(() => {
        const syncBlock = selectNode && findNormalOrSyncBlock(selectNode, blocks, syncComponents)
        const block = selectNode && findBlockById<IncludeFloatBoxBlockOfBlock>(selectNode.id, blocks)
        const isSyncScope = syncBlock?.isMasterSynchronous || syncBlock?.isLeafSynchronous
        return [syncBlock, block, isSyncScope]
    }, [blocks, selectNode, syncComponents])

    const floatBlockList = useMemo(() => {
        const block = syncContainerBlock ?? containerBlock
        if (!block) {
            return
        }
        const floatIds = value ?? []
        return isSyncScope
            ? reduce<string, FloatBlockAbstract[]>(
                  (p, c) => {
                      const block = findBlockById<FloatBlockAbstract>(c, syncComponents)
                      return block ? [...p, block] : p
                  },
                  [],
                  floatIds
              )
            : reduce<string, FloatBlockAbstract[]>(
                  (p, c) => {
                      const block = findBlockById<FloatBlockAbstract>(c, blocks)
                      return block ? [...p, block] : p
                  },
                  [],
                  floatIds
              )
    }, [syncContainerBlock, containerBlock, value, isSyncScope, syncComponents, blocks])

    const isEmpty = floatBlockList?.length === 0

    const isExistFixed = floatBlockList?.some(block => block.config.breakPoint.overlay.overlayType === 'fixed')

    const { onRemove } = useNodeToolbarActions({
        stackId,
        pageId,
        clearSelected: false
    })

    const handleGetViewFirstRecordId = useAtomCallback(
        useCallback(
            (get, set) => {
                const customView = get(selectedBlockCustomViewAtom)
                const viewId = customView?.id ?? ''
                const dsId = customView?.config?.pointer ?? ''

                return get(viewIndependentDataAtomFamily({ stackId, viewId, dsId })).recordPoints[0]?.id
            },
            [stackId]
        )
    )

    const handleCreateFloatBlock = useAtomCallback(
        useCallback(
            async (get, set, showOn: 'click' | 'hover', overlayType: 'relative' | 'fixed') => {
                const getContainerState = (containerId: string) =>
                    get(containerRuntimeStateAtomFamily({ stackId, containerId })) || undefined

                const newContainerBlock = mergeDeepRight<BlockAbstract>(
                    getBlockInitConfig(mainBreakPoint, {
                        type: 'container',
                        subType: 'vertical'
                    }) ?? {},
                    {
                        config: {
                            breakPoint: {
                                design: {
                                    background: {
                                        type: 'color',
                                        color: '#ffffff'
                                    },
                                    border: {
                                        type: 'color',
                                        color: '#E4E7EC'
                                    },
                                    radius: [12, 12, 12, 12]
                                },
                                size: {
                                    width: {
                                        size: 360,
                                        unit: 'px'
                                    },
                                    overflow: 'hidden'
                                },
                                layout: {
                                    align: {
                                        alignX: FLEX_ALIGN['flex-start'],
                                        alignY: FLEX_ALIGN['center']
                                    }
                                }
                            }
                        }
                    }
                )

                const floatBlock: FloatBlockAbstract = {
                    id: `floatBox-${nanoid(12)}`,
                    type: 'floatBox',
                    title: overlayType === 'relative' ? '弹出相对浮层' : '弹出固定浮层',
                    config: generateFloatBlock(mainBreakPoint, showOn, overlayType),
                    children: overlayType === 'fixed' && newContainerBlock ? [newContainerBlock] : []
                }

                let parentSyncBlockId: string | undefined
                if (isSyncScope) {
                    let finallySyncContainerBlock: ContainerBlockAbstract[] | undefined
                    setSyncBlocks(syncBlocks => {
                        let children: BlockAbstract[] | undefined = []
                        const syncBlock = findBlockById<IncludeFloatBoxBlockOfBlock>(syncContainerBlock?.id ?? '', syncBlocks)
                        floatBlock.isMasterSynchronous = false
                        floatBlock.isLeafSynchronous = true
                        newContainerBlock.isMasterSynchronous = false
                        newContainerBlock.isLeafSynchronous = true
                        if (!syncBlock) {
                            return
                        }
                        syncBlock.config.floatPointerList = [...(syncBlock.config.floatPointerList ?? []), floatBlock.id]
                        if (syncBlock?.isMasterSynchronous) {
                            children =
                                syncBlock && containerBlock
                                    ? getCurrentBlockChildren(syncBlock, { getContainerState, uniqueContainerId: containerBlock.id })
                                    : undefined
                        } else {
                            children = syncBlock
                                ? getCurrentBlockChildren(syncBlock, {
                                      getContainerState,
                                      uniqueContainerId: `${currentScope}@${syncBlock?.id}`
                                  })
                                : undefined
                        }
                        if (!children) {
                            return
                        }
                        floatBlock.isLeafSynchronous = true
                        children.push(floatBlock)
                        finallySyncContainerBlock = clone(syncBlocks)
                    })
                    if (!finallySyncContainerBlock) {
                        return
                    }
                    await createSyncBlock({
                        createBlocks: [floatBlock],
                        blocks: finallySyncContainerBlock
                    })

                    // await updateSyncComponents(finallySyncContainerBlock)

                    setBlocks(draft => {
                        const pageBlocks = clone(draft[pageId]) as BlockAbstract[]
                        set(initContainerRuntimeStateAtom, {
                            stackId,
                            blocks: pageBlocks,
                            syncComponents: finallySyncContainerBlock || []
                        })
                    })

                    parentSyncBlockId = syncContainerBlock?.isMasterSynchronous ? containerBlock?.id : currentScope
                    if (containerBlock && finallySyncContainerBlock) {
                        pageUndoRedoController.add({
                            action: NODE_UPDATE_ACTION,
                            payload: {
                                created: {
                                    createdSyncComponents: [floatBlock],
                                    prevSyncComponents: syncComponents,
                                    nextSyncComponents: finallySyncContainerBlock
                                }
                            }
                        })
                    }
                } else {
                    let finallyBlocks: BlockAbstract[] | undefined
                    let finallyBlock: BlockAbstract | undefined
                    setBlocks(draft => {
                        const pageBlocks = draft[pageId]
                        if (!pageBlocks) {
                            return
                        }
                        const parentBlock = findBlockById<IncludeFloatBoxBlockOfBlock>(blockId, pageBlocks)
                        if (!parentBlock) {
                            return
                        }
                        parentBlock.config.floatPointerList = [...(parentBlock.config.floatPointerList ?? []), floatBlock.id]
                        const children = parentBlock ? getCurrentBlockChildren(parentBlock, { getContainerState }) : pageBlocks
                        if (!children) {
                            return
                        }
                        children.push(floatBlock)
                        finallyBlocks = clone(pageBlocks)
                        finallyBlock = clone(parentBlock)
                    })

                    if (!finallyBlocks || !containerBlock || !finallyBlock) {
                        return
                    }
                    await createBlock({
                        pageId,
                        stackId,
                        blocks: finallyBlocks,
                        createdBlocks: [floatBlock]
                    })
                    updateRemoteBlock({
                        appId,
                        block: finallyBlock,
                        origin: containerBlock,
                        blocks: finallyBlocks,
                        pageId,
                        stackId
                    })
                    pageUndoRedoController.add({
                        action: NODE_UPDATE_ACTION,
                        payload: {
                            created: {
                                createdBlocks: [floatBlock],
                                prevBlocks: blocks,
                                nextBlocks: finallyBlocks
                            }
                        }
                    })
                }

                openFloatBox({
                    blockId: floatBlock.id,
                    stackId,
                    open: true,
                    scope: parentSyncBlockId,
                    recordId: handleGetViewFirstRecordId()
                })
            },
            [
                appId,
                blockId,
                blocks,
                containerBlock,
                createBlock,
                currentScope,
                handleGetViewFirstRecordId,
                isSyncScope,
                mainBreakPoint,
                openFloatBox,
                pageId,
                setBlocks,
                setSyncBlocks,
                stackId,
                syncComponents,
                syncContainerBlock?.id,
                syncContainerBlock?.isMasterSynchronous,
                updateRemoteBlock
            ]
        )
    )

    return (
        <Group4ByecodeUi
            label="浮层"
            styles={{
                collapse: {
                    padding: '0 8px !important'
                }
            }}
            extra={
                (floatBlockList ?? []).length < 2 && (
                    <div onClick={e => e.stopPropagation()}>
                        <Menu position="bottom-end" width={200}>
                            <Menu.Target>
                                <TagIcon
                                    icon="Add"
                                    size={24}
                                    enableHover
                                    hoverBackground="#26415A0F"
                                    color="var(--color-gray-400)"
                                    radius={4}
                                />
                            </Menu.Target>
                            <Menu.Dropdown>
                                <Menu position="left-start" trigger="hover" width={200}>
                                    <Menu.Target>
                                        <Menu.Item
                                            disabled={floatBlockList?.some(v => v.config.breakPoint.overlay.showOn === 'hover')}
                                            rightSection={<IconFont type="ArrowRightSmall" />}
                                        >
                                            鼠标移入
                                        </Menu.Item>
                                    </Menu.Target>
                                    <Menu.Dropdown>
                                        <Menu.Item onClick={() => handleCreateFloatBlock('hover', 'relative')}>弹出相对浮层</Menu.Item>
                                        <Menu.Item disabled={isExistFixed} onClick={() => handleCreateFloatBlock('hover', 'fixed')}>
                                            弹出固定浮层
                                        </Menu.Item>
                                    </Menu.Dropdown>
                                </Menu>

                                <Menu position="left-start" trigger="hover" width={200}>
                                    <Menu.Target>
                                        <Menu.Item
                                            disabled={floatBlockList?.some(v => v.config.breakPoint.overlay.showOn === 'click')}
                                            rightSection={<IconFont type="ArrowRightSmall" />}
                                        >
                                            鼠标点击
                                        </Menu.Item>
                                    </Menu.Target>
                                    <Menu.Dropdown>
                                        <Menu.Item onClick={() => handleCreateFloatBlock('click', 'relative')}>弹出相对浮层</Menu.Item>
                                        <Menu.Item disabled={isExistFixed} onClick={() => handleCreateFloatBlock('click', 'fixed')}>
                                            弹出固定浮层
                                        </Menu.Item>
                                    </Menu.Dropdown>
                                </Menu>
                            </Menu.Dropdown>
                        </Menu>
                    </div>
                )
            }
        >
            {isEmpty ? (
                <SCxContainer>
                    <Empty
                        icon="Copy"
                        styles={{
                            root: {
                                height: 130
                            },
                            wrapper: {
                                gap: 12
                            }
                        }}
                        description="添加浮层可实现弹窗、下拉列表等场景"
                    />
                </SCxContainer>
            ) : (
                floatBlockList?.map(floatBlock => (
                    <ListItem4ByecodeUi key={floatBlock?.id} justifyContent="space-between" alignItems="center">
                        <Text>{floatBlock.config.breakPoint.overlay.showOn === 'hover' ? '鼠标移入' : '鼠标点击'}</Text>
                        <Input
                            style={{ width: 180 }}
                            styles={{
                                input: {
                                    cursor: 'pointer'
                                }
                            }}
                            value={floatBlock?.title}
                            readOnly
                            prefix={
                                <TagIcon icon="Copy" size={24} iconColor="#fff" background="var(--color-theme-7)" radius={4} color="#fff" />
                            }
                            suffix={
                                <IconFont
                                    type="CloseCircle"
                                    size={16}
                                    onClick={e => {
                                        e.stopPropagation()
                                        // if (isSyncScope) {
                                        //     setSyncBlocks(syncBlocks => {
                                        //         const syncBlock = findBlockById(syncContainerBlock?.id ?? '', syncBlocks)
                                        //         if (!syncBlock) {
                                        //             return
                                        //         }
                                        //         let children: BlockAbstract[] | undefined = []
                                        //         if (syncBlock.isMasterSynchronous) {
                                        //             children = containerBlock
                                        //                 ? getCurrentBlockChildren(syncBlock, {
                                        //                       blockRuntimeState,
                                        //                       uniqueContainerId: containerBlock.id
                                        //                   })
                                        //                 : undefined
                                        //         } else {
                                        //             children = getCurrentBlockChildren(syncBlock, {
                                        //                 blockRuntimeState,
                                        //                 uniqueContainerId: `${currentScope}@${syncBlock?.id}`
                                        //             })
                                        //         }
                                        //         const index = (children ?? []).findIndex(v => v.id === floatBlock.id)
                                        //         if (index !== -1) {
                                        //             children?.splice(index, 1)
                                        //         }
                                        //     })

                                        //     deleteSyncLeafBlocks({ pageId, removedBlocks, blocks })
                                        // } else {
                                        onRemove([{ id: floatBlock.id, scope: currentScope }])
                                        // }
                                    }}
                                />
                            }
                            onClick={() => {
                                openFloatBox({
                                    blockId: floatBlock.id,
                                    stackId,
                                    open: true,
                                    scope: isSyncScope ? currentScope ?? containerBlock?.id : undefined,
                                    recordId: handleGetViewFirstRecordId()
                                })
                            }}
                        />
                    </ListItem4ByecodeUi>
                ))
            )}
        </Group4ByecodeUi>
    )
}
